import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Flex,
  Icon,
  IconButton,
  Menu,
  Portal,
  Text,
} from "@chakra-ui/react-v3";
import { buildDayjs } from "@metriport/shared/common/date";
import { ReactNode } from "react";
import { FaChevronDown } from "react-icons/fa";
import { FiMapPin, FiUser } from "react-icons/fi";
import { RiAddLine, RiCheckLine } from "react-icons/ri";
import { Tooltip } from "../../../ui/tooltip";
import { useDownloadFile } from "../../shared-logic/useDownloadFile";
import { Z_INDEX } from "../constants/z-index";
import { useDocumentCallbackContext } from "../side-panels/sidepanel-context";

export type ResourceCardLayoutProps = {
  date: string;
  isFirst?: boolean;
  docId?: string;
  resourceType?: string;
  resources?: unknown[];
  badge?: {
    text: string;
    colorPalette: string;
  };
  facilityName?: string;
  providerName?: string;
  children: ReactNode;
};

/**
 * Reusable card layout for timeline resource items.
 * Provides consistent structure with header (date, badge, actions),
 * body content slot, and optional footer (facility/provider metadata).
 */
export function ResourceCardLayout({
  date,
  isFirst = false,
  docId,
  resourceType,
  resources,
  badge,
  facilityName,
  providerName,
  children,
}: ResourceCardLayoutProps) {
  const { onAddToExport, onRemoveFromExport, isDocumentInExportList, onPdfView } =
    useDocumentCallbackContext();
  const { onDownloadFile, isDownloading } = useDownloadFile();

  const formattedDate = buildDayjs(date).format("MMM D, YYYY");
  const isMarkedForExport = docId ? isDocumentInExportList(docId) : false;

  function handleDownload(conversionType?: "html" | "pdf") {
    if (!docId) return;
    onDownloadFile({
      docId,
      docFilename: docId,
      conversionType,
    });
  }

  function handlePdfView() {
    if (!docId) return;
    onPdfView(docId, docId, undefined);
  }

  function handleToggleExport() {
    if (!docId) return;
    if (isDocumentInExportList(docId)) {
      onRemoveFromExport(docId);
    } else {
      onAddToExport(docId, docId);
    }
  }

  const hasFooter = facilityName || providerName;

  return (
    <Box
      borderWidth="1px"
      borderColor="border.emphasized"
      borderRadius="md"
      bg="bg.subtle"
      overflow="hidden"
    >
      {/* Header: Date, Badge, and Actions */}
      <Flex
        justify="space-between"
        align="center"
        px={3}
        py={2}
        borderBottomWidth="1px"
        borderColor="border"
        bg="bg.panel"
      >
        <Flex align="center" gap={2}>
          <Text
            color={isFirst ? "colorPalette.700" : "fg.emphasized"}
            fontWeight={isFirst ? "semibold" : "medium"}
            fontSize="sm"
          >
            {formattedDate}
          </Text>

          {badge && (
            <Badge colorPalette={badge.colorPalette} size="sm">
              {badge.text}
            </Badge>
          )}
        </Flex>

        {docId && (
          <DocumentActions
            docId={docId}
            resourceType={resourceType}
            resources={resources}
            isMarkedForExport={isMarkedForExport}
            onToggleExport={handleToggleExport}
            onPdfView={handlePdfView}
            onDownload={handleDownload}
            isDownloading={isDownloading}
          />
        )}
      </Flex>

      {/* Main content slot */}
      <Box p={3}>{children}</Box>

      {/* Footer: Facility and Provider metadata */}
      {hasFooter && (
        <Box borderTopWidth="1px" borderColor="border" bg="bg.panel" px={3} py={2}>
          <Flex gap={4} wrap="wrap" fontSize="xs" color="fg.muted">
            {facilityName && (
              <Flex gap={1} alignItems="center">
                <Icon fontSize="xs">
                  <FiMapPin />
                </Icon>
                <Text>{facilityName}</Text>
              </Flex>
            )}
            {providerName && (
              <Flex gap={1} alignItems="center">
                <Icon fontSize="xs">
                  <FiUser />
                </Icon>
                <Text>{providerName}</Text>
              </Flex>
            )}
          </Flex>
        </Box>
      )}
    </Box>
  );
}

export type DocumentActionsProps = {
  docId: string;
  resourceType?: string;
  resources?: unknown[];
  isMarkedForExport: boolean;
  onToggleExport: () => void;
  onPdfView: () => void;
  onDownload: (conversionType?: "html" | "pdf") => void;
  isDownloading: Record<string, { downloading: boolean; type?: string }>;
};

/**
 * Reusable document action buttons for resource cards.
 * Includes mark for export button and view/download menu.
 */
export function DocumentActions({
  docId,
  isMarkedForExport,
  onToggleExport,
  onPdfView,
  onDownload,
  isDownloading,
}: DocumentActionsProps) {
  return (
    <Flex gap={2} alignItems="center">
      <Tooltip content="Mark this document for export" positioning={{ placement: "top" }}>
        <IconButton
          size="sm"
          variant={isMarkedForExport ? "solid" : "outline"}
          bg={isMarkedForExport ? "btn.success.bg" : "transparent"}
          color={isMarkedForExport ? "btn.success.color" : "fg.muted"}
          borderColor={isMarkedForExport ? "btn.success.bg" : "border.subtle"}
          _hover={{
            backgroundColor: isMarkedForExport ? "btn.success.hover.bg" : "bg.muted",
            borderColor: isMarkedForExport ? "btn.success.hover.bg" : "border.emphasized",
          }}
          onClick={onToggleExport}
          aria-label={isMarkedForExport ? "Remove from export" : "Mark for export"}
        >
          {isMarkedForExport ? <RiCheckLine /> : <RiAddLine />}
        </IconButton>
      </Tooltip>

      <Menu.Root positioning={{ placement: "bottom-start" }}>
        <ButtonGroup size="sm" variant="outline" attached>
          <Button variant="outline" _hover={{ backgroundColor: "bg.muted" }} onClick={onPdfView}>
            View PDF
          </Button>
          <Menu.Trigger asChild>
            <IconButton
              variant="outline"
              borderLeftRadius={0}
              _hover={{ backgroundColor: "bg.muted" }}
            >
              <FaChevronDown />
            </IconButton>
          </Menu.Trigger>
        </ButtonGroup>
        <Portal>
          <Menu.Positioner>
            <Menu.Content zIndex={Z_INDEX.SIDE_PANEL_RESOURCE_DOCUMENTS_MENU}>
              <Menu.Item
                value="html"
                onClick={() => onDownload("html")}
                cursor="pointer"
                disabled={
                  isDownloading[docId]?.downloading && isDownloading[docId]?.type === "html"
                }
              >
                {isDownloading[docId]?.downloading && isDownloading[docId]?.type === "html"
                  ? "Downloading HTML..."
                  : "Download HTML"}
              </Menu.Item>
              <Menu.Item
                value="xml"
                onClick={() => onDownload(undefined)}
                cursor="pointer"
                disabled={isDownloading[docId]?.downloading && isDownloading[docId]?.type === "xml"}
              >
                {isDownloading[docId]?.downloading && isDownloading[docId]?.type === "xml"
                  ? "Downloading XML..."
                  : "Download XML"}
              </Menu.Item>
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>
    </Flex>
  );
}
